import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useMenu } from '../context/MenuContext';
import { useServiceTimes } from '../context/ServiceTimesContext'; // ✅ NEUE IMPORT
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

const { FiArrowLeft, FiMail, FiPhone, FiUsers, FiClock, FiCheck, FiGift, FiAward, FiAlertCircle, FiLoader, FiCalendar, FiInfo } = FiIcons;

const RegistrationForm = () => {
  const { day } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    weeklyMenus, 
    nextWeeklyMenus, 
    addRegistration, 
    calculateBenefits, 
    calculateNextBenefitThreshold,
    emailServiceReady, 
    isMenuOrderable, 
    getOrderableDaysForWeeklyMenu, 
    getCustomWeekDates 
  } = useMenu();

  // ✅ NEUE SERVICE TIMES INTEGRATION
  const { getDefaultServiceTime, getActiveServiceTimes, formatServiceTime } = useServiceTimes();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    persons: 1,
    time: '', // ✅ WIRD DYNAMISCH GESETZT
    preference: 'suppe',
    contactMethod: 'email'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [benefitInfo, setBenefitInfo] = useState({
    personsNeeded: 3,
    benefit: 'Kostenloser Kaffee',
    progress: 25
  });
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');

  // ✅ SETZE STANDARD-SERVICEZEIT beim Laden
  useEffect(() => {
    const defaultTime = getDefaultServiceTime();
    if (defaultTime && defaultTime.time && !formData.time) {
      setFormData(prev => ({
        ...prev,
        time: defaultTime.time
      }));
    }
  }, [getDefaultServiceTime, formData.time]);

  // URL-Erkennung für Hash-Router
  const currentPath = location.pathname;
  const isWeeklyRegistration = day === 'woche';
  const isNextWeekRegistration = day === 'next-woche' || day?.startsWith('next-');
  const isFlexibleRegistration = currentPath.includes('/flexible/') || currentPath.includes('/next-flexible/');

  console.log('🔥 DEBUGGING Registration Form:', {
    day,
    currentPath,
    locationPathname: location.pathname,
    isWeeklyRegistration,
    isNextWeekRegistration,
    isFlexibleRegistration
  });

  // Extrahiere den tatsächlichen Tag
  let actualDay;
  if (isFlexibleRegistration) {
    actualDay = day;
  } else {
    actualDay = isNextWeekRegistration && day !== 'next-woche' ? day.replace('next-', '') : day;
  }

  console.log('🎯 Extracted actualDay:', actualDay);

  // Bestimme das richtige Menü
  const currentMenu = isWeeklyRegistration || day === 'next-woche' 
    ? null 
    : isNextWeekRegistration 
      ? nextWeeklyMenus[actualDay] 
      : weeklyMenus[actualDay];

  // Bestellbare Tage
  const orderableDays = isFlexibleRegistration 
    ? getOrderableDaysForWeeklyMenu(isNextWeekRegistration, actualDay) 
    : getOrderableDaysForWeeklyMenu(isNextWeekRegistration);

  // Hole die Daten für die 5-Tage-Menübuchung
  const customWeekDates = isFlexibleRegistration 
    ? getCustomWeekDates(actualDay, isNextWeekRegistration) 
    : null;

  console.log('🎯 Menu and dates debug:', {
    currentMenu: !!currentMenu,
    orderableDays: orderableDays.length,
    customWeekDates: customWeekDates ? Object.keys(customWeekDates).length : 0,
    isFlexibleRegistration,
    actualDay
  });

  // WEITERLEITUNG - Prüfung ob Menüs geladen sind
  const [menusLoaded, setMenusLoaded] = useState(false);
  useEffect(() => {
    const hasCurrentMenus = Object.keys(weeklyMenus).length > 0;
    const hasNextMenus = Object.keys(nextWeeklyMenus).length > 0;
    if (hasCurrentMenus || hasNextMenus) {
      setMenusLoaded(true);
    }
  }, [weeklyMenus, nextWeeklyMenus]);

  // Weiterleitung nur für NICHT-FLEXIBLE Buchungen
  useEffect(() => {
    if (!menusLoaded) {
      console.log('Menüs werden noch geladen, warte...');
      return;
    }

    if (isFlexibleRegistration) {
      console.log('✅ Flexible Buchung erkannt - keine Weiterleitung');
      return;
    }

    console.log('Prüfe Weiterleitung für NICHT-flexible Buchung:', {
      day,
      isWeeklyRegistration,
      isNextWeekRegistration,
      actualDay,
      currentMenu: !!currentMenu,
      orderableDays: orderableDays.length,
      menusLoaded
    });

    // Für Tagesmenüs (nicht flexibel und nicht wöchentlich)
    if (!isWeeklyRegistration && day !== 'next-woche') {
      if (!currentMenu) {
        console.log('Kein Menü gefunden für Tag:', actualDay, 'isNextWeek:', isNextWeekRegistration);
        if (isNextWeekRegistration) {
          const hasAnyNextWeekMenus = Object.keys(nextWeeklyMenus).length > 0;
          if (!hasAnyNextWeekMenus) {
            console.log('Keine Menüs für nächste Woche verfügbar');
            navigate('/naechste-woche');
            return;
          } else {
            console.log('Nächste Woche Menüs verfügbar, aber nicht für Tag:', actualDay);
            navigate('/naechste-woche');
            return;
          }
        } else {
          navigate('/');
          return;
        }
      }

      if (!isNextWeekRegistration && !currentMenu.isOrderable) {
        console.log('Menü ist nicht bestellbar:', actualDay);
        navigate('/');
        return;
      }
    }
    // Für Wochenmenüs (NICHT flexible)
    else if (isWeeklyRegistration || day === 'next-woche') {
      const menuCollection = isNextWeekRegistration ? nextWeeklyMenus : weeklyMenus;
      const orderableDaysCount = Object.values(menuCollection).filter(menu =>
        isNextWeekRegistration || menu.isOrderable
      ).length;

      if (orderableDaysCount < 2) {
        console.log('Zu wenige bestellbare Tage für Wochenmenü:', orderableDaysCount);
        navigate(isNextWeekRegistration ? '/naechste-woche' : '/');
        return;
      }
    }
  }, [
    currentMenu,
    isWeeklyRegistration,
    isNextWeekRegistration,
    isFlexibleRegistration,
    navigate,
    day,
    weeklyMenus,
    nextWeeklyMenus,
    orderableDays,
    menusLoaded,
    actualDay
  ]);

  // Update benefit info when persons change
  useEffect(() => {
    const nextBenefitInfo = calculateNextBenefitThreshold(
      formData.persons,
      isWeeklyRegistration || day === 'next-woche' || isFlexibleRegistration
    );
    setBenefitInfo(nextBenefitInfo);
  }, [formData.persons, isWeeklyRegistration, calculateNextBenefitThreshold, day, isFlexibleRegistration]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name ist erforderlich';
    }

    if (formData.contactMethod === 'email') {
      if (!formData.email.trim()) {
        newErrors.email = 'E-Mail ist erforderlich';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Ungültige E-Mail-Adresse';
      }
    } else {
      if (!formData.phone.trim()) {
        newErrors.phone = 'Telefonnummer ist erforderlich';
      } else if (!/^\+?[\d\s\-()]+$/.test(formData.phone)) {
        newErrors.phone = 'Ungültige Telefonnummer';
      }
    }

    if (formData.persons < 1 || formData.persons > 20) {
      newErrors.persons = 'Anzahl Personen muss zwischen 1 und 20 liegen';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');
    setSubmitSuccess('');

    try {
      // Für Wochenanmeldungen die bestellbaren Tage ermitteln
      let registrationDayText = '';
      let menuNameText = '';
      let totalPrice = 0;
      let startDay = null;
      let daysCount = 0;

      if (isWeeklyRegistration || day === 'next-woche') {
        // Standard-Wochenmenü
        const daysText = orderableDays.map(d => 
          d.charAt(0).toUpperCase() + d.slice(1)
        ).join(', ');
        
        registrationDayText = isNextWeekRegistration 
          ? `Nächste Woche (${daysText})` 
          : `Diese Woche (${daysText})`;
        
        menuNameText = isNextWeekRegistration 
          ? 'Wochenmenü (nächste Woche)' 
          : 'Wochenmenü';

        const pricePerDay = 18.50;
        daysCount = orderableDays.length;
        totalPrice = pricePerDay * daysCount * formData.persons;

      } else if (isFlexibleRegistration) {
        // FLEXIBLE 5-TAGE-ANMELDUNG ab einem bestimmten Tag
        startDay = actualDay;
        const fiveDayDates = customWeekDates || {};
        const daysText = Object.keys(fiveDayDates)
          .map(d => d.charAt(0).toUpperCase() + d.slice(1))
          .join(', ');
        
        const startDayText = startDay.charAt(0).toUpperCase() + startDay.slice(1);
        
        registrationDayText = isNextWeekRegistration 
          ? `5 Tage ab ${startDayText} (nächste Woche): ${daysText}`
          : `5 Tage ab ${startDayText}: ${daysText}`;
        
        menuNameText = isNextWeekRegistration 
          ? `5 Tage ab ${startDayText} (nächste Woche)`
          : `5 Tage ab ${startDayText}`;

        const pricePerDay = 18.50;
        daysCount = 5;
        totalPrice = pricePerDay * daysCount * formData.persons;

      } else {
        // Einzelne Tagesanmeldung
        registrationDayText = currentMenu.day.charAt(0).toUpperCase() + currentMenu.day.slice(1) + 
          (currentMenu.formattedDate ? ` - ${currentMenu.formattedDate}` : '');
        menuNameText = currentMenu.name;
        daysCount = 1;
        totalPrice = currentMenu.price * formData.persons;
      }

      // Vorbereitung der Anmeldedaten
      const registration = {
        ...formData,
        day: registrationDayText,
        menuName: menuNameText,
        totalPrice: totalPrice,
        benefits: calculateBenefits(
          formData.persons,
          isWeeklyRegistration || day === 'next-woche' || isFlexibleRegistration
        ),
        registrationDate: new Date().toISOString(),
        menuDate: isWeeklyRegistration || day === 'next-woche' || isFlexibleRegistration 
          ? null 
          : currentMenu.date,
        isNextWeek: isNextWeekRegistration || day === 'next-woche',
        startDay: startDay,
        isFlexibleWeek: isFlexibleRegistration,
        daysCount: daysCount
      };

      console.log('✅ Anmeldung wird übermittelt:', registration);

      const result = await addRegistration(registration);
      console.log('Anmeldungsergebnis:', result);

      if (result.error) {
        throw new Error(result.error.message || 'Unbekannter Fehler bei der Anmeldung');
      }

      // Erfolgsmeldung anzeigen
      if (formData.contactMethod === 'email') {
        setSubmitSuccess(`Ihre Anmeldung wurde erfolgreich gespeichert und eine Bestätigung wurde an ${formData.email} gesendet.`);
      } else {
        setSubmitSuccess('Ihre Anmeldung wurde erfolgreich gespeichert. Wir werden Sie bei Bedarf telefonisch kontaktieren.');
      }

      setTimeout(() => {
        setIsSubmitted(true);
        setIsSubmitting(false);
      }, 1000);

    } catch (error) {
      console.error('Fehler bei der Anmeldung:', error);
      setSubmitError('Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut oder kontaktieren Sie uns telefonisch.');
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'persons' ? parseInt(value) || 1 : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const benefits = calculateBenefits(
    formData.persons,
    isWeeklyRegistration || day === 'next-woche' || isFlexibleRegistration
  );

  // Berechne den Gesamtpreis
  let totalPrice = 0;
  let daysCount = 0;

  if (isWeeklyRegistration || day === 'next-woche') {
    const pricePerDay = 18.50;
    daysCount = orderableDays.length;
    totalPrice = pricePerDay * daysCount * formData.persons;
  } else if (isFlexibleRegistration) {
    const pricePerDay = 18.50;
    daysCount = 5;
    totalPrice = pricePerDay * daysCount * formData.persons;
  } else {
    daysCount = 1;
    totalPrice = currentMenu?.price * formData.persons || 0;
  }

  // ✅ HOLE AKTIVE SERVICEZEITEN für Dropdown
  const activeServiceTimes = getActiveServiceTimes();

  // Zeige Loading-Zustand während Menüs geladen werden
  if (!menusLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center"
        >
          <div className="flex items-center justify-center mb-4">
            <SafeIcon icon={FiLoader} className="text-primary-600 text-3xl animate-spin mr-3" />
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">
            Menüs werden geladen...
          </h2>
          <p className="text-gray-600">
            Bitte warten Sie einen Moment.
          </p>
        </motion.div>
      </div>
    );
  }

  // Wenn der Benutzer bereits eine Anmeldung abgeschickt hat
  if (isSubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center"
        >
          <div className="bg-green-100 p-4 rounded-full w-20 h-20 mx-auto mb-6 flex items-center justify-center">
            <SafeIcon icon={FiCheck} className="text-green-600 text-3xl" />
          </div>

          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Anmeldung erfolgreich!
          </h2>

          <p className="text-gray-600 mb-6">
            Vielen Dank für Ihre Anmeldung.
          </p>

          {submitSuccess && (
            <div className="bg-green-50 border border-green-200 text-green-800 p-3 rounded-lg mb-6">
              {submitSuccess}
            </div>
          )}

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/')}
            className="bg-primary-500 text-white px-8 py-3 rounded-xl font-semibold hover:bg-primary-600 transition-colors duration-300"
          >
            Zurück zur Übersicht
          </motion.button>
        </motion.div>
      </div>
    );
  }

  // Bestimme den Titel für die Anmeldung
  let registrationTitle = '';
  if (isWeeklyRegistration) {
    registrationTitle = day === 'next-woche' ? 'Wochenanmeldung (nächste Woche)' : 'Wochenanmeldung';
  } else if (isFlexibleRegistration) {
    const startDayText = actualDay.charAt(0).toUpperCase() + actualDay.slice(1);
    registrationTitle = isNextWeekRegistration 
      ? `5 Tage ab ${startDayText} (nächste Woche)` 
      : `5 Tage ab ${startDayText}`;
  }

  // Generiere eine Vorschau der 5-Tage-Menübuchung
  const flexibleDaysPreview = isFlexibleRegistration && customWeekDates 
    ? Object.entries(customWeekDates).map(([day, date]) => {
        const formattedDate = format(parseISO(date), 'd. MMMM', { locale: de });
        return `${day.charAt(0).toUpperCase() + day.slice(1)} (${formattedDate})`;
      }).join(', ')
    : '';

  console.log('🎯 FINAL RENDER DEBUG:', {
    isFlexibleRegistration,
    registrationTitle,
    flexibleDaysPreview,
    customWeekDates: customWeekDates ? Object.keys(customWeekDates) : 'null',
    totalPrice,
    daysCount,
    activeServiceTimes: activeServiceTimes.length
  });

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center mb-8"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate(isNextWeekRegistration ? '/naechste-woche' : '/')}
            className="flex items-center space-x-2 text-primary-600 hover:text-primary-700 font-semibold"
          >
            <SafeIcon icon={FiArrowLeft} />
            <span>Zurück zur Übersicht</span>
          </motion.button>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Menu Info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl shadow-xl p-6"
          >
            {/* KORREKTE ANZEIGE FÜR FLEXIBLE BUCHUNGEN */}
            {(isWeeklyRegistration || day === 'next-woche' || isFlexibleRegistration) ? (
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  {registrationTitle}
                </h2>

                {/* SPEZIELLE ANZEIGE FÜR FLEXIBLE 5-TAGE-BUCHUNG */}
                {isFlexibleRegistration ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center mb-2">
                      <SafeIcon icon={FiCalendar} className="text-green-600 mr-2" />
                      <h3 className="font-medium text-green-800">
                        5 Arbeitstage ab {actualDay.charAt(0).toUpperCase() + actualDay.slice(1)}
                      </h3>
                    </div>
                    <p className="text-green-700 mb-3">
                      Ihre Buchung umfasst 5 aufeinanderfolgende Arbeitstage (Mo-Fr) beginnend mit{' '}
                      {actualDay.charAt(0).toUpperCase() + actualDay.slice(1)} oder dem nächsten verfügbaren Arbeitstag.
                    </p>
                    <p className="text-green-700">
                      <strong>Gebuchte Tage:</strong> {flexibleDaysPreview}
                    </p>
                  </div>
                ) : (
                  <p className="text-gray-600 mb-3">
                    Bestellbare Tage: {orderableDays.map(d => 
                      d.charAt(0).toUpperCase() + d.slice(1)
                    ).join(', ')}
                  </p>
                )}

                {/* Wochenmenü-Vorteile Hinweis */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center mb-2">
                    <SafeIcon icon={FiInfo} className="text-green-600 mr-2" />
                    <h3 className="font-medium text-green-800">Ihre Vorteile:</h3>
                  </div>
                  <ul className="space-y-2 ml-6 text-green-700">
                    {isWeeklyRegistration && (
                      <li className="flex items-start">
                        <SafeIcon icon={FiCheck} className="text-green-600 mr-2 mt-1 flex-shrink-0" />
                        <span>Kostenloses Dessert bei Wochenbestellung</span>
                      </li>
                    )}
                    <li className="flex items-start">
                      <SafeIcon icon={FiCheck} className="text-green-600 mr-2 mt-1 flex-shrink-0" />
                      <span>
                        Zeitsparende Anmeldung für {isFlexibleRegistration ? 5 : orderableDays.length} Tage
                      </span>
                    </li>
                    <li className="flex items-start">
                      <SafeIcon icon={FiCheck} className="text-green-600 mr-2 mt-1 flex-shrink-0" />
                      <span>Garantierte Platzreservierung für alle Tage</span>
                    </li>
                  </ul>
                </div>

                <p className="text-gray-600 mb-6">
                  {isFlexibleRegistration ? 5 : orderableDays.length} Tagesmenüs zum Preis von je CHF 18.50
                </p>

                <div className="space-y-3">
                  {/* Zeige die Menüs der bestellbaren Tage für flexible Buchungen */}
                  {isFlexibleRegistration ? (
                    // Für flexible 5-Tage-Buchungen: Zeige alle 5 Tage
                    customWeekDates && Object.entries(customWeekDates).map(([dayName, date]) => {
                      const menuCollection = isNextWeekRegistration ? nextWeeklyMenus : weeklyMenus;
                      const menu = menuCollection[dayName];

                      if (menu) {
                        return (
                          <div key={dayName} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                            <img src={menu.image} alt={menu.name} className="w-12 h-12 rounded-lg object-cover" />
                            <div>
                              <div className="font-semibold text-sm">
                                {menu.day.charAt(0).toUpperCase() + menu.day.slice(1)} - {format(parseISO(date), 'd. MMMM', { locale: de })}
                              </div>
                              <div className="text-xs text-gray-600">{menu.name}</div>
                            </div>
                          </div>
                        );
                      } else {
                        return (
                          <div key={dayName} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg opacity-70">
                            <div className="w-12 h-12 rounded-lg bg-gray-200 flex items-center justify-center">
                              <SafeIcon icon={FiCalendar} className="text-gray-400" />
                            </div>
                            <div>
                              <div className="font-semibold text-sm">
                                {dayName.charAt(0).toUpperCase() + dayName.slice(1)} - {format(parseISO(date), 'd. MMMM', { locale: de })}
                              </div>
                              <div className="text-xs text-gray-600">Menü wird später festgelegt</div>
                            </div>
                          </div>
                        );
                      }
                    })
                  ) : (
                    // Für normale Wochenbuchungen: Zeige nur die bestellbaren Tage
                    orderableDays.map((dayName) => {
                      const menuCollection = isNextWeekRegistration ? nextWeeklyMenus : weeklyMenus;
                      const menu = menuCollection[dayName];

                      if (!menu) {
                        return (
                          <div key={dayName} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                            <div className="w-12 h-12 rounded-lg bg-gray-200 flex items-center justify-center">
                              <SafeIcon icon={FiCalendar} className="text-gray-400" />
                            </div>
                            <div>
                              <div className="font-semibold text-sm">
                                {dayName.charAt(0).toUpperCase() + dayName.slice(1)}
                              </div>
                              <div className="text-xs text-gray-600">Menü wird später festgelegt</div>
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div key={dayName} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                          <img src={menu.image} alt={menu.name} className="w-12 h-12 rounded-lg object-cover" />
                          <div>
                            <div className="font-semibold text-sm">
                              {menu.day.charAt(0).toUpperCase() + menu.day.slice(1)}{' '}
                              {menu.formattedDate ? ` - ${menu.formattedDate}` : ''}
                            </div>
                            <div className="text-xs text-gray-600">{menu.name}</div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            ) : currentMenu ? (
              <div>
                <img src={currentMenu.image} alt={currentMenu.name} className="w-full h-48 object-cover rounded-xl mb-4" />

                <div className="flex items-center space-x-2 mb-2">
                  <SafeIcon icon={FiCalendar} className="text-primary-600" />
                  <h3 className="text-lg font-medium text-gray-700">
                    {currentMenu.day.charAt(0).toUpperCase() + currentMenu.day.slice(1)}{' '}
                    {currentMenu.formattedDate ? ` - ${currentMenu.formattedDate}` : ''}
                    {currentMenu.isToday && (
                      <span className="ml-2 text-sm bg-green-500 text-white px-2 py-0.5 rounded-full">
                        Heute
                      </span>
                    )}
                    {isNextWeekRegistration && (
                      <span className="ml-2 text-sm bg-blue-500 text-white px-2 py-0.5 rounded-full">
                        Nächste Woche
                      </span>
                    )}
                  </h3>
                </div>

                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                  {currentMenu.name}
                </h2>

                <p className="text-gray-600 mb-4">
                  {currentMenu.description}
                </p>

                <div className="flex items-center justify-between">
                  <span className="text-xl font-bold text-primary-600">
                    CHF {currentMenu.price.toFixed(2)}
                  </span>
                  <div className="flex items-center text-gray-500">
                    <SafeIcon icon={FiClock} className="mr-1" />
                    <span>Standard: {formatServiceTime(getDefaultServiceTime().time)}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-500">
                Menü nicht gefunden
              </div>
            )}

            {/* Benefits */}
            {benefits.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6 p-4 bg-green-50 rounded-xl border border-green-200"
              >
                <div className="flex items-center space-x-2 mb-2">
                  <SafeIcon icon={FiGift} className="text-green-600" />
                  <span className="font-semibold text-green-800">Ihre Vorteile:</span>
                </div>
                <ul className="space-y-1">
                  {benefits.map((benefit, index) => (
                    <li key={index} className="text-sm text-green-700 flex items-center space-x-2">
                      <SafeIcon icon={FiCheck} className="text-green-600 text-xs" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            )}

            {/* Benefits Progress Bar */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-200"
            >
              <div className="flex items-center space-x-2 mb-2">
                <SafeIcon icon={FiAward} className="text-blue-600" />
                <span className="font-semibold text-blue-800">Vorteile-Fortschritt:</span>
              </div>

              <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
                <div 
                  className="bg-blue-600 h-4 rounded-full transition-all duration-500 ease-in-out"
                  style={{ width: `${benefitInfo.progress}%` }}
                ></div>
              </div>

              {benefitInfo.personsNeeded > 0 ? (
                <p className="text-sm text-blue-700">
                  <strong>Noch {benefitInfo.personsNeeded} {benefitInfo.personsNeeded === 1 ? 'Person' : 'Personen'}</strong> bis zum nächsten Vorteil: {benefitInfo.benefit}
                </p>
              ) : (
                <p className="text-sm font-semibold text-blue-700">
                  {benefitInfo.benefit}
                </p>
              )}
            </motion.div>
          </motion.div>

          {/* Registration Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl shadow-xl p-6"
          >
            <h3 className="text-xl font-bold text-gray-800 mb-6">
              Anmeldung
              {isNextWeekRegistration && (
                <span className="ml-2 text-sm bg-blue-500 text-white px-2 py-0.5 rounded-full">
                  Nächste Woche
                </span>
              )}
              {/* BADGE FÜR FLEXIBLE BUCHUNG */}
              {isFlexibleRegistration && (
                <span className="ml-2 text-sm bg-green-500 text-white px-2 py-0.5 rounded-full">
                  5-Tage-Menü
                </span>
              )}
            </h3>

            {submitError && (
              <div className="mb-6 p-4 bg-red-50 rounded-xl text-red-700 flex items-center">
                <SafeIcon icon={FiAlertCircle} className="mr-2" />
                <span>{submitError}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Ihr Name"
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
              </div>

              {/* Contact Method */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Kontakt-Methode *
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, contactMethod: 'email' }))}
                    className={`flex items-center justify-center space-x-2 py-3 px-4 rounded-xl border transition-colors ${
                      formData.contactMethod === 'email'
                        ? 'bg-primary-500 text-white border-primary-500'
                        : 'bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100'
                    }`}
                  >
                    <SafeIcon icon={FiMail} />
                    <span>E-Mail</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, contactMethod: 'phone' }))}
                    className={`flex items-center justify-center space-x-2 py-3 px-4 rounded-xl border transition-colors ${
                      formData.contactMethod === 'phone'
                        ? 'bg-primary-500 text-white border-primary-500'
                        : 'bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100'
                    }`}
                  >
                    <SafeIcon icon={FiPhone} />
                    <span>Telefon</span>
                  </button>
                </div>
              </div>

              {/* Email or Phone */}
              {formData.contactMethod === 'email' ? (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    E-Mail-Adresse *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="ihre@email.com"
                  />
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Telefonnummer *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                      errors.phone ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="+41 79 123 45 67"
                  />
                  {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                </div>
              )}

              {/* Number of Persons */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Anzahl Personen *
                </label>
                <div className="relative">
                  <SafeIcon icon={FiUsers} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="number"
                    name="persons"
                    value={formData.persons}
                    onChange={handleInputChange}
                    min="1"
                    max="20"
                    className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                      errors.persons ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                </div>
                {errors.persons && <p className="text-red-500 text-sm mt-1">{errors.persons}</p>}
              </div>

              {/* ✅ NEUE SERVICEZEITEN-AUSWAHL */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Gewünschte Servicezeit
                </label>
                <div className="relative">
                  <SafeIcon icon={FiClock} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  {activeServiceTimes.length > 1 ? (
                    <select
                      name="time"
                      value={formData.time}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    >
                      {activeServiceTimes.map((serviceTime) => (
                        <option key={serviceTime.id} value={serviceTime.time}>
                          {serviceTime.label} ({formatServiceTime(serviceTime.time)})
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="time"
                      name="time"
                      value={formData.time}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    />
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {activeServiceTimes.length > 1 
                    ? 'Wählen Sie Ihre gewünschte Servicezeit aus' 
                    : `Standard: ${formatServiceTime(getDefaultServiceTime().time)}`}
                </p>
              </div>

              {/* Soup or Salad */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Vorspeise
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, preference: 'suppe' }))}
                    className={`py-3 px-4 rounded-xl border transition-colors ${
                      formData.preference === 'suppe'
                        ? 'bg-primary-500 text-white border-primary-500'
                        : 'bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100'
                    }`}
                  >
                    Suppe
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, preference: 'salat' }))}
                    className={`py-3 px-4 rounded-xl border transition-colors ${
                      formData.preference === 'salat'
                        ? 'bg-primary-500 text-white border-primary-500'
                        : 'bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100'
                    }`}
                  >
                    Salat
                  </button>
                </div>
              </div>

              {/* Total Price */}
              <div className="bg-gray-50 p-4 rounded-xl">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-gray-700">Gesamtpreis:</span>
                  <span className="text-2xl font-bold text-primary-600">
                    CHF {totalPrice.toFixed(2)}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {/* KORRIGIERTE PREISANZEIGE */}
                  {isFlexibleRegistration 
                    ? `5 Menüs × CHF 18.50 × ${formData.persons} Person(en)`
                    : isWeeklyRegistration || day === 'next-woche'
                      ? `${orderableDays.length} Menüs × CHF 18.50 × ${formData.persons} Person(en)`
                      : `${formData.persons} Person(en) × CHF ${currentMenu?.price.toFixed(2)}`
                  }
                </p>
              </div>

              {/* Submit Button */}
              <motion.button
                type="submit"
                disabled={isSubmitting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`w-full bg-primary-500 text-white py-4 rounded-xl font-semibold text-lg hover:bg-primary-600 transition-colors duration-300 flex items-center justify-center space-x-2 ${
                  isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                }`}
              >
                {isSubmitting ? (
                  <>
                    <SafeIcon icon={FiLoader} className="animate-spin" />
                    <span>Wird gesendet...</span>
                  </>
                ) : (
                  <>
                    <span>Anmeldung abschicken</span>
                    <SafeIcon icon={FiCheck} />
                  </>
                )}
              </motion.button>
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default RegistrationForm;